/**
 * Setting insulation area properties with polygon tool
 * @2026/02/12
 *
 * @log first created for insulation area feature
 */

import { useState, useEffect, ChangeEvent } from 'react';

import {
  getInsulationAreaSetting as initIA,
  cacheInsulationAreaSetting as cache,
} from '../state';
import { TKEvent, InsulationAreaSetting } from '../types';
import { type AreaType } from '../types/insulation';

export type { AreaType } from '../types/insulation';

/**
 * properties defined in admin-panel
 *
 * @returns
 */
export const useInsulationAreaSetting = (selectType: AreaType) => {
  // initialize insulation area settings from cache:
  // IAS holds { areaMode, polygon: InsulationAreaSetting, default: InsulationAreaSetting }
  const IAS = initIA();
  const modeSettings = IAS[IAS.areaMode];

  // area mode: polygon or default
  const [areaMode, setAreaMode] = useState<AreaType>(IAS.areaMode);
  // serve category: DUCT or PIPE - @2026/04/14
  const [serveCategory, setServeCategory] = useState<'DUCT' | 'PIPE'>(
    modeSettings.serveCategory,
  );

  // area type
  const [exposeType, setExposeType] = useState(modeSettings.exposeType);

  // polygon area name prefix, fallback to `areaName` - @2026/06/05
  const [namePrefix, setNamePrefix] = useState(
    modeSettings.namePrefix || modeSettings.areaName,
  );

  // cache current mode's settings on every render
  cache({
    areaMode,
    serveCategory,
    exposeType,
    namePrefix,
  });

  useEffect(() => {
    setAreaMode(selectType);
  }, [selectType]);

  const categoryChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setServeCategory(event.target.value as 'DUCT' | 'PIPE');
  };

  const exposeTypeChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
    setExposeType(event.target.value);
  };

  const namePrefixChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setNamePrefix(event.target.value);
  };

  /**
   * Cache an insulation area setting into map editor to create polygon
   */
  const applyInsulationAreaSettingHandler = () => {
    const detail: InsulationAreaSetting = {
      areaName: namePrefix,
      areaMode,
      serveCategory,
      exposeType,
    };
    document.dispatchEvent(
      new CustomEvent(TKEvent.APPLY_POLYGON_AREA_SETTING, { detail }),
    );
  };

  /**
   * @deprecated no longer in use
   */
  const applyInsulationDefaultSettingHandler = () => {
    const detail: InsulationAreaSetting = {
      areaName: 'default', // ! create a `default` area setting to save!
      areaMode,
      serveCategory,
      exposeType,
    };
    document.dispatchEvent(
      new CustomEvent(TKEvent.APPLY_POLYGON_AREA_SETTING, { detail }),
    );
  };

  return {
    areaMode,
    serveCategory,
    /** area type */
    exposeType,
    namePrefix,
    categoryChangeHandler,
    exposeTypeChangeHandler,
    namePrefixChangeHandler,
    applyInsulationAreaSettingHandler,
    applyInsulationDefaultSettingHandler,
  };
};
